import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { submitAuthorization, fetchAuthorizations } from '../../services/docuvaultService';

const {
  FiX, FiShield, FiCheck, FiUser, FiMail, FiAlertTriangle,
  FiKey, FiClock, FiRefreshCw, FiLock, FiEdit3, FiInfo,
} = FiIcons;

const ROLES = ['executor', 'beneficiary', 'attorney', 'trustee', 'co-trustee', 'other'];

// ── Signature Preview ─────────────────────────────────────────────────────────
function SignaturePreview({ text }) {
  return (
    <div className="relative h-20 bg-white rounded-2xl border-2 border-dashed border-accent-gold/40 flex items-center justify-center overflow-hidden">
      {/* Baseline rule */}
      <div className="absolute bottom-5 left-6 right-6 h-px bg-accent-navy/10" />
      <AnimatePresence mode="wait">
        {text.trim() ? (
          <motion.p
            key={text}
            initial={{ opacity: 0, y: 6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0 }}
            className="text-2xl text-accent-navy px-6 pb-2 truncate max-w-full"
            style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", letterSpacing: '0.03em' }}
          >
            {text}
          </motion.p>
        ) : (
          <motion.p
            key="placeholder"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="text-sm text-text-muted/50 font-light italic select-none"
          >
            Your signature will appear here…
          </motion.p>
        )}
      </AnimatePresence>
      {/* Corner watermark */}
      {text.trim() && (
        <div className="absolute top-2 right-3 flex items-center gap-1">
          <SafeIcon icon={FiLock} className="w-2.5 h-2.5 text-accent-gold/40" />
          <span className="text-[8px] font-black uppercase tracking-widest text-accent-gold/40">Signed</span>
        </div>
      )}
    </div>
  );
}

// ── Audit Log Entry ───────────────────────────────────────────────────────────
function AuditEntry({ log }) {
  return (
    <div className="flex items-start gap-3 p-3 bg-brand-light rounded-xl border border-brand-dark">
      <div className="w-7 h-7 bg-green-100 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
        <SafeIcon icon={FiCheck} className="w-3.5 h-3.5 text-green-600" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-[10px] font-bold text-accent-navy">
          {log.approver_name}
          <span className="text-text-muted font-light"> · {log.approver_role}</span>
        </p>
        <p className="text-[9px] text-text-muted">{log.approver_email}</p>
        {log.typed_signature && (
          <p
            className="text-sm text-accent-navy/70 mt-0.5 truncate"
            style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive" }}
            title={log.typed_signature}
          >
            {log.typed_signature}
          </p>
        )}
        <p className="text-[9px] font-mono text-text-muted/60 truncate mt-0.5">{log.signature}</p>
      </div>
      <p className="text-[9px] text-text-muted flex-shrink-0">
        {new Date(log.created_at).toLocaleDateString()}
      </p>
    </div>
  );
}

// ── Main Modal ────────────────────────────────────────────────────────────────
const DocuVaultAuthModal = ({ isOpen, onClose, document, onSuccess }) => {
  const [name,          setName]          = useState('');
  const [email,         setEmail]         = useState('');
  const [role,          setRole]          = useState('executor');
  const [notes,         setNotes]         = useState('');
  const [typedSig,      setTypedSig]      = useState('');
  const [sigConfirmed,  setSigConfirmed]  = useState(false);
  const [loading,       setLoading]       = useState(false);
  const [done,          setDone]          = useState(false);
  const [error,         setError]         = useState('');
  const [result,        setResult]        = useState(null);
  const [authLogs,      setAuthLogs]      = useState([]);
  const [logsLoading,   setLogsLoading]   = useState(false);
  const sigRef = useRef(null);

  // Derived document state
  const currentApprovals = (() => {
    try { return JSON.parse(document?.authorization_approvals || '[]'); } catch { return []; }
  })();
  const authorizedParties = (() => {
    try { return JSON.parse(document?.authorized_parties || '[]'); } catch { return []; }
  })();

  const threshold         = document?.authorization_threshold || 1;
  const approvalCount     = result ? result.approvalCount : currentApprovals.length;
  const progress          = Math.min((approvalCount / threshold) * 100, 100);
  const isFullyAuthorized = result ? result.isFullyAuthorized : (approvalCount >= threshold);

  // Load audit log on open
  useEffect(() => {
    if (!isOpen || !document?.id) return;
    setLogsLoading(true);
    fetchAuthorizations(document.id)
      .then(logs => setAuthLogs(Array.isArray(logs) ? logs : []))
      .catch(() => setAuthLogs([]))
      .finally(() => setLogsLoading(false));
  }, [isOpen, document?.id]);

  // Auto-populate signature when name changes (if sig not yet customised)
  useEffect(() => {
    if (!typedSig || typedSig === '') setTypedSig(name);
  }, [name]);

  const canSubmit = name.trim().length >= 2
    && email.trim().length > 0
    && typedSig.trim().length >= 2
    && sigConfirmed
    && !loading;

  const handleSubmit = async () => {
    if (!canSubmit) {
      if (!sigConfirmed)           setError('Please confirm your digital signature by ticking the checkbox.');
      else if (!typedSig.trim())   setError('A typed signature is required.');
      return;
    }
    setLoading(true); setError('');

    try {
      const res = await submitAuthorization(
        document.id,
        currentApprovals,
        {
          name:          name.trim(),
          email:         email.trim().toLowerCase(),
          role,
          notes,
          typedSignature: typedSig.trim(),
        },
        document.s3_key
      );
      setResult(res);
      setDone(true);

      // Refresh audit log
      fetchAuthorizations(document.id)
        .then(logs => setAuthLogs(Array.isArray(logs) ? logs : []))
        .catch(() => {});

      setTimeout(() => { onSuccess?.(); }, 2500);
    } catch (e) {
      setError(e.message || 'Authorization failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setName(''); setEmail(''); setRole('executor'); setNotes('');
    setTypedSig(''); setSigConfirmed(false);
    setLoading(false); setDone(false); setError(''); setResult(null);
    onClose();
  };

  if (!isOpen || !document) return null;

  return (
    <div className="fixed inset-0 z-[210] flex items-center justify-center p-4 bg-accent-navy/70 backdrop-blur-md">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="bg-white rounded-[40px] shadow-2xl w-full max-w-lg overflow-hidden border border-brand-dark max-h-[92vh] flex flex-col"
      >
        {/* ── Header ─────────────────────────────────────────────────────── */}
        <div className="p-7 bg-gradient-to-r from-purple-900 to-accent-navy flex items-center justify-between flex-shrink-0">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-purple-400/30 text-white rounded-2xl flex items-center justify-center">
              <SafeIcon icon={FiShield} className="w-6 h-6" />
            </div>
            <div>
              <h3 className="text-xl font-serif text-white">Multi-Party Authorization</h3>
              <p className="text-white/50 text-[10px] font-black uppercase tracking-widest">
                Cryptographically Logged · AWS S3 Linked
              </p>
            </div>
          </div>
          <button onClick={handleClose} className="p-2 text-white/60 hover:text-white rounded-xl transition-colors">
            <SafeIcon icon={FiX} className="w-5 h-5" />
          </button>
        </div>

        {/* ── Scrollable body ─────────────────────────────────────────────── */}
        <div className="flex-1 overflow-y-auto p-8 space-y-6">

          {/* Document info */}
          <div className="p-4 bg-brand-light rounded-2xl border border-brand-dark">
            <p className="text-[9px] font-black uppercase tracking-widest text-text-muted mb-1">Document</p>
            <p className="text-sm font-bold text-accent-navy truncate">{document.original_name}</p>
            {document.s3_key && (
              <p className="text-[10px] font-mono text-text-muted mt-0.5 truncate">
                S3: {document.s3_key.split('/').pop()}
              </p>
            )}
          </div>

          {/* Authorization progress */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                Authorization Progress
              </p>
              <p className={`text-[10px] font-black ${isFullyAuthorized ? 'text-green-600' : 'text-accent-gold'}`}>
                {approvalCount} / {threshold} Approvals
              </p>
            </div>
            <div className="h-2.5 bg-brand-light rounded-full overflow-hidden border border-brand-dark">
              <motion.div
                animate={{ width: `${progress}%` }}
                transition={{ duration: 0.5 }}
                className={`h-full rounded-full ${isFullyAuthorized ? 'bg-green-500' : 'bg-accent-gold'}`}
              />
            </div>
            {isFullyAuthorized && (
              <div className="mt-2 flex items-center gap-2 text-[10px] font-black text-green-600">
                <SafeIcon icon={FiCheck} className="w-3.5 h-3.5" />
                Authorization threshold reached — document fully authorized.
              </div>
            )}
          </div>

          {/* ── Success state ─────────────────────────────────────────────── */}
          {done && result && (
            <motion.div
              initial={{ opacity: 0, y: 8 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-5 bg-green-50 border border-green-200 rounded-2xl space-y-4"
            >
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                  <SafeIcon icon={FiCheck} className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-sm font-bold text-green-800">Authorization Submitted!</p>
                  <p className="text-[10px] text-green-600 font-light">{result.message}</p>
                </div>
              </div>

              {/* Signature receipt */}
              <div className="bg-white rounded-xl border border-green-100 p-4 space-y-2">
                <p className="text-[9px] font-black uppercase tracking-widest text-text-muted">
                  Signature Receipt
                </p>
                <p
                  className="text-2xl text-accent-navy"
                  style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive" }}
                >
                  {typedSig}
                </p>
                <div className="h-px bg-accent-navy/10 w-full" />
                <div className="grid grid-cols-2 gap-2 text-[10px]">
                  <div>
                    <p className="text-text-muted font-black uppercase tracking-widest mb-0.5">Auth Record ID</p>
                    <p className="font-bold text-accent-navy">#{result.authRecordId}</p>
                  </div>
                  <div>
                    <p className="text-text-muted font-black uppercase tracking-widest mb-0.5">Crypto Signature</p>
                    <p className="font-mono text-accent-navy truncate">{result.signature?.slice(0, 18)}…</p>
                  </div>
                </div>
              </div>
            </motion.div>
          )}

          {/* ── Authorization form ────────────────────────────────────────── */}
          {!done && (
            <div className="space-y-5">

              {/* Identity fields */}
              <div className="space-y-3">
                <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy">
                  Identity
                </p>
                <div className="relative">
                  <SafeIcon icon={FiUser} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="text"
                    value={name}
                    onChange={e => setName(e.target.value)}
                    placeholder="Your Full Legal Name"
                    className="w-full pl-11 pr-4 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                  />
                </div>
                <div className="relative">
                  <SafeIcon icon={FiMail} className="absolute left-4 top-1/2 -translate-y-1/2 text-text-muted w-4 h-4" />
                  <input
                    type="email"
                    value={email}
                    onChange={e => setEmail(e.target.value)}
                    placeholder="Your Email Address"
                    className="w-full pl-11 pr-4 py-3.5 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-sm"
                  />
                </div>
              </div>

              {/* Role selector */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">
                  Your Role
                </label>
                <div className="grid grid-cols-3 gap-2">
                  {ROLES.map(r => (
                    <button
                      key={r}
                      onClick={() => setRole(r)}
                      className={`py-2 rounded-xl text-[9px] font-black uppercase tracking-widest capitalize transition-all border-2 ${
                        role === r
                          ? 'border-accent-gold bg-accent-gold/5 text-accent-navy'
                          : 'border-brand-dark text-text-muted hover:border-accent-gold/50'
                      }`}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              </div>

              {/* ── Digital Signature Section ─────────────────────────────── */}
              <div className="space-y-3">
                <div className="flex items-center justify-between">
                  <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy flex items-center gap-1.5">
                    <SafeIcon icon={FiEdit3} className="w-3.5 h-3.5 text-accent-gold" />
                    Digital Signature
                    <span className="text-red-400">*</span>
                  </p>
                  <div className="group relative">
                    <SafeIcon icon={FiInfo} className="w-3.5 h-3.5 text-text-muted cursor-help" />
                    <div className="absolute right-0 top-5 w-56 bg-accent-navy text-white text-[9px] font-light leading-relaxed p-3 rounded-xl shadow-xl opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                      Your typed name serves as a legally binding digital signature. It is cryptographically
                      hashed, timestamped, and permanently linked to this S3 document record.
                    </div>
                  </div>
                </div>

                {/* Typed signature input */}
                <div className="relative">
                  <SafeIcon icon={FiEdit3} className="absolute left-4 top-1/2 -translate-y-1/2 text-accent-gold w-4 h-4" />
                  <input
                    ref={sigRef}
                    type="text"
                    value={typedSig}
                    onChange={e => setTypedSig(e.target.value)}
                    placeholder="Type your full name as your signature"
                    maxLength={120}
                    className="w-full pl-11 pr-4 py-3.5 bg-amber-50 border-2 border-accent-gold/40 focus:border-accent-gold rounded-2xl outline-none text-sm font-medium"
                    style={{ fontFamily: "'Dancing Script', 'Brush Script MT', cursive", fontSize: '1rem' }}
                  />
                  {typedSig.length > 0 && (
                    <span className="absolute right-4 top-1/2 -translate-y-1/2 text-[9px] text-text-muted">
                      {typedSig.length}/120
                    </span>
                  )}
                </div>

                {/* Live signature preview */}
                <SignaturePreview text={typedSig} />

                {/* Legal disclaimer + confirmation checkbox */}
                <div className={`p-4 rounded-2xl border-2 transition-all ${
                  sigConfirmed
                    ? 'bg-green-50 border-green-200'
                    : 'bg-amber-50/60 border-accent-gold/20'
                }`}>
                  <label className="flex items-start gap-3 cursor-pointer select-none">
                    <div className="relative flex-shrink-0 mt-0.5">
                      <input
                        type="checkbox"
                        checked={sigConfirmed}
                        onChange={e => setSigConfirmed(e.target.checked)}
                        className="sr-only"
                      />
                      <div
                        onClick={() => setSigConfirmed(v => !v)}
                        className={`w-5 h-5 rounded-md border-2 flex items-center justify-center transition-all cursor-pointer ${
                          sigConfirmed
                            ? 'bg-green-500 border-green-500'
                            : 'bg-white border-accent-gold/50 hover:border-accent-gold'
                        }`}
                      >
                        {sigConfirmed && <SafeIcon icon={FiCheck} className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                    <p className="text-[10px] text-text-muted font-light leading-relaxed">
                      I, <strong className="text-accent-navy">{typedSig.trim() || '(your name)'}</strong>, confirm
                      that this typed signature constitutes my legal authorization of the above document.
                      I understand this action is <strong className="text-accent-navy">permanent, timestamped,
                      and cryptographically recorded</strong> against the S3 object and cannot be revoked.
                    </p>
                  </label>
                </div>
              </div>

              {/* Notes */}
              <div>
                <label className="text-[10px] font-black uppercase tracking-widest text-accent-navy block mb-1.5">
                  Notes (Optional)
                </label>
                <textarea
                  value={notes}
                  onChange={e => setNotes(e.target.value)}
                  rows={2}
                  maxLength={2000}
                  placeholder="Any comments about this authorization…"
                  className="w-full px-4 py-3 bg-brand-light border-2 border-brand-dark rounded-2xl outline-none focus:border-accent-gold text-xs resize-none"
                />
                <p className="text-right text-[9px] text-text-muted mt-1">{notes.length}/2000</p>
              </div>

              {/* Authorized parties list */}
              {authorizedParties.length > 0 && (
                <div className="p-3 bg-purple-50 border border-purple-100 rounded-2xl">
                  <p className="text-[9px] font-black uppercase tracking-widest text-purple-700 mb-2">
                    Authorized Parties
                  </p>
                  <div className="space-y-1.5">
                    {authorizedParties.map((p, i) => {
                      const approved = currentApprovals.find(
                        a => a.email?.toLowerCase() === p.email?.toLowerCase()
                      );
                      return (
                        <div key={i} className="flex items-center justify-between text-[10px]">
                          <span className="font-bold text-purple-800">
                            {p.name}
                            <span className="text-purple-500 font-light"> ({p.role})</span>
                          </span>
                          {approved
                            ? <span className="flex items-center gap-1 text-green-600 font-black">
                                <SafeIcon icon={FiCheck} className="w-3 h-3" /> Approved
                              </span>
                            : <span className="flex items-center gap-1 text-text-muted font-light">
                                <SafeIcon icon={FiClock} className="w-3 h-3" /> Pending
                              </span>
                          }
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Validation requirements checklist */}
              <div className="grid grid-cols-2 gap-2">
                {[
                  { ok: name.trim().length >= 2,     label: 'Full name entered'        },
                  { ok: email.includes('@'),          label: 'Valid email'              },
                  { ok: typedSig.trim().length >= 2,  label: 'Signature typed'         },
                  { ok: sigConfirmed,                 label: 'Signature confirmed'      },
                ].map(({ ok, label }) => (
                  <div key={label} className={`flex items-center gap-2 text-[9px] font-bold ${ok ? 'text-green-600' : 'text-text-muted'}`}>
                    <div className={`w-4 h-4 rounded-full flex items-center justify-center flex-shrink-0 ${ok ? 'bg-green-100' : 'bg-brand-dark'}`}>
                      {ok
                        ? <SafeIcon icon={FiCheck} className="w-2.5 h-2.5 text-green-600" />
                        : <div className="w-1.5 h-1.5 rounded-full bg-text-muted/30" />
                      }
                    </div>
                    {label}
                  </div>
                ))}
              </div>

              {/* Error banner */}
              {error && (
                <motion.div
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="flex items-start gap-2 p-3 bg-red-50 border border-red-100 rounded-xl text-xs text-red-700"
                >
                  <SafeIcon icon={FiAlertTriangle} className="w-4 h-4 flex-shrink-0 mt-0.5" />
                  {error}
                </motion.div>
              )}
            </div>
          )}

          {/* ── Audit Log ─────────────────────────────────────────────────── */}
          {authLogs.length > 0 && (
            <div>
              <p className="text-[10px] font-black uppercase tracking-widest text-accent-navy mb-2 flex items-center gap-1.5">
                <SafeIcon icon={FiKey} className="w-3.5 h-3.5" /> Authorization Audit Log
              </p>
              <div className="space-y-2 max-h-48 overflow-y-auto pr-1">
                {authLogs.map((log, i) => <AuditEntry key={i} log={log} />)}
              </div>
            </div>
          )}

          {logsLoading && (
            <div className="flex items-center gap-2 text-[10px] text-text-muted">
              <SafeIcon icon={FiRefreshCw} className="w-3.5 h-3.5 animate-spin" /> Loading audit log…
            </div>
          )}
        </div>

        {/* ── Footer ─────────────────────────────────────────────────────── */}
        <div className="p-6 border-t border-brand-light flex-shrink-0">
          {done ? (
            <button
              onClick={handleClose}
              className="w-full py-4 bg-green-500 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest hover:bg-green-600 transition-all"
            >
              Close
            </button>
          ) : (
            <button
              onClick={handleSubmit}
              disabled={!canSubmit}
              title={!sigConfirmed ? 'Please confirm your signature first' : !typedSig.trim() ? 'Type your signature' : ''}
              className={`w-full flex items-center justify-center gap-2 py-4 rounded-2xl text-[10px] font-black uppercase tracking-widest transition-all ${
                canSubmit
                  ? 'bg-accent-navy text-white hover:bg-accent-navy/90 shadow-lg shadow-accent-navy/10'
                  : 'bg-brand-dark text-text-muted cursor-not-allowed'
              }`}
            >
              {loading
                ? <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                : <>
                    <SafeIcon icon={FiShield} className="w-4 h-4" />
                    Submit Authorization
                  </>
              }
            </button>
          )}
        </div>
      </motion.div>
    </div>
  );
};

export default DocuVaultAuthModal;